/**
 * The staged-crew model (#340 item 4, deepened by sweep 2 Pass D).
 *
 * CrewSection stages a full crew locally, then saves it in one replace call —
 * the highest-stakes shaping in the workspace: the endpoint is a full crew
 * replace, so a row dropped here is an assignment cancelled on the server.
 * This module owns the whole staged model, not just the wire payload: the
 * state (`StagedCrew`), every transition on it, the derived row list the
 * table renders, and the three commercial rules — pay, frozen-ness, and
 * validity — that used to exist twice (here and privately in the component).
 *
 * The rules, in domain terms:
 *   - staged rows (the operator's local edits) win — their pay is computed
 *     as rate × hours until offers go out;
 *   - once any active offer has gone out (`commercialsFrozen`), commercial
 *     fields on existing Assignments stay frozen while task, individual-note,
 *     and job-sheet-template edits can still save;
 *   - active notified assignments NOT re-staged are preserved verbatim so a
 *     partial edit never cancels the rest of the crew;
 *   - rows the operator cancelled or removed this session are dropped from
 *     both groups.
 *
 * Validation is part of the contract: any editable row with a non-positive
 * rate/hours makes the whole save invalid (nothing is sent) — the same
 * `crewRowInvalid` the component renders per-row — and an empty result means
 * there is nothing to save, NOT "replace the crew with nobody".
 */
import { isActive } from "./assignment-status";
import { isPositiveNumber } from "@shared/validators";
import type { CrewAssignmentPayload } from "./event-workspace-api";

/** One staged (locally edited, not yet saved) crew row, keyed by contractor. */
/**
 * Assignment pay: rate × hours, computed in integer cents × hundredths of an
 * hour so the same figure can never round differently between the crew
 * editor's live pay column, the contractor payment section, and the save
 * payload (sweep 2 D6 — previously three formulas).
 */
export function calculateAssignmentPay(
  hourlyRate: number,
  hours: number,
): number {
  const rateCents = Math.round(hourlyRate * 100);
  const hundredthsOfAnHour = Math.round(hours * 100);
  return Math.round((rateCents * hundredthsOfAnHour) / 100) / 100;
}

export interface StagedCrewRow {
  contractor_id: string;
  hourly_rate: string;
  estimated_hours: string;
  tasks_description: string;
  /** Job sheet template override id; "" = inherit the default (#188). */
  jobsheet_template_id: string;
  /** Vehicle this Contractor drives; "" = none (migration 120). */
  vehicle_name: string;
}

/** The slice of a server assignment the model reads. */
export interface ExistingCrewRow {
  id: string;
  contractorId: string;
  status: string;
  hourlyRate: number | null;
  estimatedHours: number | null;
  payAmount: number | null;
  tasksDescription: string | null;
  jobsheetTemplateId: string | null;
  vehicleName: string | null;
}

/**
 * The whole staged-crew state, as one value. The three pieces move together
 * through the transitions below — never mutate one on its own.
 */
export interface StagedCrew {
  /** Staged rows keyed by contractor id. */
  staged: Record<string, StagedCrewRow>;
  /** Assignment ids cancelled server-side this session (pre-refetch overlay). */
  cancelledIds: ReadonlySet<string>;
  /** Contractor ids removed from the staged set this session. */
  removedContractorIds: ReadonlySet<string>;
}

/** Seed the staged map from the server's active assignments. */
export function initStagedCrew(existing: ExistingCrewRow[]): StagedCrew {
  const staged: Record<string, StagedCrewRow> = {};
  for (const a of existing) {
    if (a.contractorId && isActive(a)) {
      staged[a.contractorId] = {
        contractor_id: a.contractorId,
        hourly_rate: a.hourlyRate?.toString() || "50",
        estimated_hours: a.estimatedHours?.toString() || "",
        tasks_description: a.tasksDescription || "",
        jobsheet_template_id: a.jobsheetTemplateId || "",
        vehicle_name: a.vehicleName || "",
      };
    }
  }
  return {
    staged,
    cancelledIds: new Set(),
    removedContractorIds: new Set(),
  };
}

/**
 * Stage a contractor. Hours prefill from the quote's labour total (#181):
 * the first contractor gets the full quoted hours, later ones the unassigned
 * remainder — hours flow from the quote instead of being re-keyed. Re-adding
 * a contractor cancelled or removed this session revives them.
 */
export function stageContractor(
  crew: StagedCrew,
  existing: ExistingCrewRow[],
  contractorId: string,
  quotedLabourHours: number | null,
): StagedCrew {
  let prefillHours = "1";
  if (quotedLabourHours != null && quotedLabourHours > 0) {
    const stagedHours = Object.values(crew.staged).reduce(
      (sum, a) => sum + (parseFloat(a.estimated_hours) || 0),
      0,
    );
    // Sent assignments not mirrored in the staged map (post-notify).
    const sentHours = existing
      .filter(
        (a) =>
          isActive(a) &&
          !crew.cancelledIds.has(a.id) &&
          !(a.contractorId in crew.staged),
      )
      .reduce((sum, a) => sum + (a.estimatedHours || 0), 0);
    const remainder = quotedLabourHours - stagedHours - sentHours;
    if (remainder > 0) prefillHours = String(remainder);
  }

  const cancelledIds = new Set(crew.cancelledIds);
  const prior = existing.find((a) => a.contractorId === contractorId);
  if (prior) cancelledIds.delete(prior.id);
  const removedContractorIds = new Set(crew.removedContractorIds);
  removedContractorIds.delete(contractorId);

  return {
    staged: {
      ...crew.staged,
      [contractorId]: {
        contractor_id: contractorId,
        hourly_rate: "50",
        estimated_hours: prefillHours,
        tasks_description: "",
        jobsheet_template_id: "",
        vehicle_name: "",
      },
    },
    cancelledIds,
    removedContractorIds,
  };
}

/** Drop a staged row and record the removal so a save cannot resurrect it. */
export function unstageContractor(
  crew: StagedCrew,
  contractorId: string,
): StagedCrew {
  const { [contractorId]: _removed, ...staged } = crew.staged;
  return {
    ...crew,
    staged,
    removedContractorIds: new Set([...crew.removedContractorIds, contractorId]),
  };
}

/** Edit one field on a staged row. */
export function updateStagedRow(
  crew: StagedCrew,
  contractorId: string,
  field: keyof StagedCrewRow,
  value: string,
): StagedCrew {
  return {
    ...crew,
    staged: {
      ...crew.staged,
      [contractorId]: { ...crew.staged[contractorId], [field]: value },
    },
  };
}

/**
 * Record a server-side cancel: the assignment leaves the active crew and any
 * staged mirror for its contractor goes with it (or the next save would
 * re-create the offer just cancelled).
 */
export function recordCancelledAssignment(
  crew: StagedCrew,
  existing: ExistingCrewRow[],
  assignmentId: string,
): StagedCrew {
  let staged = crew.staged;
  const cancelled = existing.find((a) => a.id === assignmentId);
  if (cancelled && cancelled.contractorId in staged) {
    const { [cancelled.contractorId]: _removed, ...rest } = staged;
    staged = rest;
  }
  return {
    ...crew,
    staged,
    cancelledIds: new Set([...crew.cancelledIds, assignmentId]),
  };
}

/** The active crew: active on the server minus this session's cancels. */
export function activeCrew<A extends ExistingCrewRow>(
  crew: StagedCrew,
  existing: A[],
): A[] {
  return existing.filter((a) => isActive(a) && !crew.cancelledIds.has(a.id));
}

/**
 * Once any active offer has gone out, commercial fields on existing
 * Assignments are frozen — the rate is on an offer the contractor has
 * already seen. The one frozen-ness predicate for display AND save.
 */
export function commercialsFrozen(
  crew: StagedCrew,
  existing: ExistingCrewRow[],
): boolean {
  return activeCrew(crew, existing).some((a) => a.status !== "pending");
}

/**
 * One row of the crew table, derived — never stored. `editable` is what used
 * to be a whole second copy of the layout: before the offers go out every row
 * is editable; after they go out the saved rows go read-only (frozen
 * commercials) while newly staged rows stay editable. A frozen row keeps its
 * staged mirror so task/note/template edits still save.
 */
export interface CrewRow<A extends ExistingCrewRow = ExistingCrewRow> {
  key: string;
  contractorId: string;
  /** The server Assignment behind this row; null on a purely staged row. */
  assignment: A | null;
  /** The staged edits; null only on a frozen row with no staged mirror. */
  staged: StagedCrewRow | null;
  /** False once the row's offer has gone out. */
  editable: boolean;
}

/**
 * Derive the table's rows. The source is deliberately branchy where the data
 * is: before notify the staged map is the whole truth (removing a row deletes
 * it from that map, so deriving off the assignments would resurrect it);
 * after notify the saved assignments are read-only rows and the staged map
 * holds only the additions.
 */
export function deriveCrewRows<A extends ExistingCrewRow>(
  crew: StagedCrew,
  existing: A[],
): CrewRow<A>[] {
  const active = activeCrew(crew, existing);
  if (!commercialsFrozen(crew, existing)) {
    return Object.entries(crew.staged).map(([id, staged]) => ({
      key: id,
      contractorId: id,
      assignment: active.find((a) => a.contractorId === id) ?? null,
      staged,
      editable: true,
    }));
  }
  return [
    ...active.map((a): CrewRow<A> => ({
      key: a.id,
      contractorId: a.contractorId,
      assignment: a,
      staged: crew.staged[a.contractorId] ?? null,
      editable: false,
    })),
    ...Object.entries(crew.staged)
      .filter(([id]) => !active.some((a) => a.contractorId === id))
      .map(([id, staged]): CrewRow<A> => ({
        key: id,
        contractorId: id,
        assignment: null,
        staged,
        editable: true,
      })),
  ];
}

/**
 * A row's estimated pay: staged rate × hours while editable; the server's pay
 * once the offer is out — a negotiated flat fee must never display (or save)
 * as rate × hours.
 */
export function crewRowPay(row: CrewRow): number | null {
  if (!row.editable || !row.staged) return row.assignment?.payAmount ?? null;
  const rate = parseFloat(row.staged.hourly_rate);
  const hours = parseFloat(row.staged.estimated_hours);
  if (!isFinite(rate) || !isFinite(hours)) return null;
  return calculateAssignmentPay(rate, hours);
}

/**
 * A row's commercial validity. Frozen rows are exempt — their commercials
 * aren't sent. The same predicate gates the per-row messages in the table and
 * the whole-save `invalid` verdict below.
 */
export function crewRowInvalid(row: CrewRow): {
  rate: boolean;
  hours: boolean;
} {
  if (!row.editable || !row.staged) return { rate: false, hours: false };
  return {
    rate: !isPositiveNumber(row.staged.hourly_rate),
    hours: !isPositiveNumber(row.staged.estimated_hours),
  };
}

/**
 * Hours assigned across the crew: staged hours on editable rows, frozen hours
 * on sent rows — the quoted-vs-assigned reconciliation input (#181).
 */
export function assignedCrewHours(rows: CrewRow[]): number {
  return rows.reduce(
    (sum, r) =>
      sum +
      (r.editable
        ? parseFloat(r.staged?.estimated_hours ?? "") || 0
        : r.assignment?.estimatedHours || 0),
    0,
  );
}

export type CrewSelectionResult =
  /** No staged rows survive the cancel filter — don't call the endpoint. */
  | { kind: "nothing-to-save" }
  /** An editable row has a non-positive rate or hours — surface validation. */
  | { kind: "invalid" }
  | { kind: "ready"; assignments: CrewAssignmentPayload[] };

/** Project the staged crew into the full-replace wire payload. */
export function buildCrewSelection(
  crew: StagedCrew,
  existing: ExistingCrewRow[],
): CrewSelectionResult {
  const { staged, cancelledIds, removedContractorIds } = crew;

  const cancelledContractorIds = new Set(
    existing.filter((a) => cancelledIds.has(a.id)).map((a) => a.contractorId),
  );
  const frozen = commercialsFrozen(crew, existing);
  const stagedRows = Object.values(staged).filter(
    (a) => !cancelledContractorIds.has(a.contractor_id),
  );
  if (stagedRows.length === 0) return { kind: "nothing-to-save" };

  const hasInvalid = deriveCrewRows(crew, existing).some((row) => {
    const invalid = crewRowInvalid(row);
    return invalid.rate || invalid.hours;
  });
  if (hasInvalid) return { kind: "invalid" };

  const preserved = existing
    .filter((a) => isActive(a) && !cancelledIds.has(a.id))
    .filter((a) => !removedContractorIds.has(a.contractorId))
    .filter((a) => !stagedRows.some((s) => s.contractor_id === a.contractorId))
    .map((a): CrewAssignmentPayload => ({
      contractor_id: a.contractorId,
      hourly_rate: a.hourlyRate || 50,
      estimated_hours: a.estimatedHours || 0,
      pay_amount: a.payAmount || 0,
      tasks_description: a.tasksDescription || null,
      jobsheet_template_id: a.jobsheetTemplateId ?? null,
      vehicle_name: a.vehicleName ?? null,
    }));

  const assignments: CrewAssignmentPayload[] = [
    ...stagedRows.map((a): CrewAssignmentPayload => {
      const existingRow = existing.find(
        (row) => row.contractorId === a.contractor_id && isActive(row),
      );
      const preserveCommercials = frozen && existingRow;
      const rate = preserveCommercials
        ? existingRow.hourlyRate || 50
        : parseFloat(a.hourly_rate) || 50;
      const hours = preserveCommercials
        ? existingRow.estimatedHours || 0
        : parseFloat(a.estimated_hours) || 0;
      return {
        contractor_id: a.contractor_id,
        hourly_rate: rate,
        estimated_hours: hours,
        pay_amount: preserveCommercials
          ? existingRow.payAmount || 0
          : calculateAssignmentPay(rate, hours),
        tasks_description: a.tasks_description || null,
        jobsheet_template_id: a.jobsheet_template_id || null,
        vehicle_name: a.vehicle_name || null,
      };
    }),
    ...preserved,
  ];

  return { kind: "ready", assignments };
}
